import React, { useEffect, useContext } from "react";
import { Box, Typography } from "@mui/material";

import AlignCenter from "../components/AlignCenter";
import { LangContext } from "../contexts/langContext";
import ReadMoreOrContinue from "../features/ReadMoreOrContinue";
import QuestionResultDescription from "../components/QuestionResultDescription";
import VideoContainer from "../features/VideoContainer";
import StreakDisplay from "../features/StreakDisplay";
import SeeQuestionAgainButton from "../features/SeeQuestionAgainBubble";
import { SlideData } from "../interfaces/question.interface";
import { WebsocketContext } from "../contexts/websocketContext";
import Fade from "../components/Fade";
import { CorrectOrIncorrect } from "@common";
import useIsMinimizedQuiz from "@natklok/hooks/useIsMinimizedQuiz";

const AnswerFeedback: React.FC<{
  answerDescription: string;
  slideData: SlideData;
  correctOrIncorrect: CorrectOrIncorrect;
  forMultiplayerHost?: boolean;
  showQuestionAgainButton?: boolean;
}> = ({
  answerDescription,
  slideData,
  correctOrIncorrect,
  forMultiplayerHost = false,
  showQuestionAgainButton = true,
}) => {
  const { translateTranslationData } = useContext(LangContext);
  const { isMultiplayerHost, inMultiplayerSession } = useContext(WebsocketContext);
  const minimized = useIsMinimizedQuiz();

  const defaultTitle =
    slideData.question === undefined
      ? ""
      : correctOrIncorrect === "correct"
      ? translateTranslationData(slideData.question.correctResultTitle)
      : translateTranslationData(slideData.question.incorrectResultTitle);

  useEffect(() => {
    // Scroll to top of page so that mobile doesn't look bad
    window.scrollTo(0, 0);
  }, []);

  const showVideo = !inMultiplayerSession || (isMultiplayerHost && inMultiplayerSession);

  return (
    <div
      style={{
        transform: minimized ? "scale(0.9)" : undefined,
        justifyContent: "center",
        display: "flex",
        flexDirection: "column",
        marginTop: "10vh",
      }}
    >
      <Box
        style={{
          justifyContent: "flex-end",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Fade>
          <AlignCenter withMaxWidth>
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <StreakDisplay />
              <Typography
                variant="h4"
                component="h2"
                textAlign="center"
                marginBottom={minimized ? 0 : 1}
              >
                {defaultTitle}
              </Typography>
              <QuestionResultDescription
                question={slideData.question}
                correctOrIncorrect={correctOrIncorrect}
                answerDescription={answerDescription}
              />
              {showVideo && !minimized && (
                <div style={{ marginTop: 16, width: "100%" }}>
                  <VideoContainer youtubeId={slideData.question?.youtubeVideoID} />
                </div>
              )}
            </div>
          </AlignCenter>
        </Fade>
      </Box>

      <ReadMoreOrContinue
        readMoreInnerHTML={slideData.question?.readMoreInnerHTML}
        onlyReadMore={forMultiplayerHost}
      />
      <Fade>
        <Box mt={2} textAlign="center">
          {forMultiplayerHost && showQuestionAgainButton && (
            <SeeQuestionAgainButton slideData={slideData} />
          )}
        </Box>
      </Fade>
    </div>
  );
};

export default AnswerFeedback;
